import React, {useContext, useEffect, useState} from 'react';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {Formik} from "formik";
import {create as createUser, update, sites as defaultSites} from "./api";
import {get as getSites} from "Sites/api";
import * as yup from "yup";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import {store} from "../store";
import {styled} from "@mui/material/styles";
import {FormElement, newFormModel} from "../molcules/forms";
import {ROLES, TEAM_ASSOCIATIONS, TIERS, getRoleLabel} from "../helpers/constants/roles";
import {getUserSubmitError, getUserSuccessMessage} from "./userErrorMessages";

const ModalForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100% - 64px)',
  overflow: 'auto'
});

const ModalFormHead = styled('div')({
  flex: '0 0 auto',
  margin: 0,
  padding: '16px 24px 20px'
});

const ModalFormH2 = styled('h2')({
  fontSize: '1.25rem',
  fontWeight: 500,
  margin: 0,
  borderBottom: '1px solid #ccc',
  padding: '8px 0 12px'
});

const ModalFormContent = styled('div')({
  flex: '1 1 auto',
  padding: '8px 24px',
  overflowY: 'auto'
});

const ModalFormFoot = styled('div')({
  flex: '0 0 auto',
  margin: 0,
  padding: '8px 24px 16px',
  display: 'flex',
  justifyContent: 'flex-end'
});

const AlertStyled = styled(Alert)({
  marginBottom: 30
});

const staffShape = (site) => ({
  email: {type: "text", id: "email", label: "Email", default: '', validation: yup.string().required('Email is required').email()},
  firstName: {type: "text", id: "firstName", label: "First Name", default: '', validation: yup.string().required('First Name is required')},
  lastName: {type: "text", id: "lastName", label: "Last Name", default: '', validation: yup.string().required('Last Name is required')},
  association: {type: "select", id: "association", label: "Association", default: 'Staff', selectValues: site.teamAssociations.map(a => ({title: a})), validation: yup.string().oneOf(site.teamAssociations)},
  position: {type: "select", id: "position", label: "Position", default: 'Team Member', selectValues: site.positions.map(p => ({title: p})), validation: yup.string().oneOf(site.positions)},
  staffAppOnly: {type: "boolean", id: "staffAppOnly", label: "Redirect to V2", default: site.pmsVendor},
  password: {default: '', hidden: true},
  sites: {default: '', hidden: true},
  role: {default: 'staff', label: 'Permissions', hidden: true},
  offRota: {type: "boolean", id: "offRota", label: "Remove from Rota", default: false},
  suspendAccount: {type: "boolean", id: "suspendAccount", label: "Suspend Account (staff member will not be able to login)", default: false}
});

const userShape = ({isCreate, currentUserRole, siteOptions}) => {
  const roleValues = Object.values(ROLES).map(r => ({value: r, title: getRoleLabel(r)}));
  const teamAssociationValues = Object.values(TEAM_ASSOCIATIONS).map(a => ({title: a}));
  const tierValues = Object.values(TIERS).map(t => ({title: t}));
  const isAdmin = currentUserRole === 'admin';
  const isSiteAdmin = currentUserRole === 'site-admin';

  return {
    email: {type: "text", id: "email", label: "Email", default: '', validation: yup.string().required('Email is required').email()},
    firstName: {type: "text", id: "firstName", label: "First Name", default: '', validation: yup.string().required('First Name is required')},
    lastName: {type: "text", id: "lastName", label: "Last Name", default: '', validation: yup.string().required('Last Name is required')},
    password: {default: '', hidden: true},
    association: {type: "select", id: "association", label: "Association", default: 'Staff', selectValues: teamAssociationValues, validation: yup.string().oneOf(Object.values(TEAM_ASSOCIATIONS))},
    ...(isAdmin ? {
      role: {type: "select", id: "role", label: "Permission Level", default: 'staff', selectValues: roleValues, validation: yup.string().oneOf(Object.values(ROLES))}
    } : {
      role: {default: 'staff', hidden: true}
    }),
    sites: siteOptions.length > 0
      ? {type: "select", id: "sites", label: "Sites", default: 'All', selectValues: siteOptions, validation: yup.string()}
      : {type: "text", id: "sites", label: "Sites", default: 'All', validation: yup.string()},
    ...((isAdmin || isSiteAdmin) ? {
      tier: {type: "select", id: "tier", label: "Tier", default: 'Free', selectValues: tierValues, validation: yup.string().oneOf(Object.values(TIERS))}
    } : {}),
    offRota: {type: "boolean", id: "offRota", label: "Off rota (hide when no shifts are assigned)", default: false},
    suspendAccount: {type: "boolean", id: "suspendAccount", label: "Suspend Account (staff member will not be able to login)", default: false},
  };
};

export default function StaffForm({user, open, onClose, onSuccess, create=false, mode='staff'}) {
  const [submitError, setSubmitError] = useState(null);
  const [shape, setShape] = useState(false);
  const [siteOptions, setSiteOptions] = useState([]);
  const {state} = useContext(store);

  useEffect(() => {
    if (mode === 'user') {
      getSites().then(res => {
        if (res.data) {
          const options = [
            defaultSites[0],
            ...res.data.map(site => ({title: site.name, value: site.normalName}))
          ];
          setSiteOptions(options);
        }
      }).catch(() => {
        setSiteOptions([]);
      });
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'user') {
      setShape(userShape({
        isCreate: create,
        currentUserRole: state.user?.role,
        siteOptions
      }));
    } else if (state.site?._id) {
      setShape(staffShape(state.site));
    }
  }, [state.site, state.user, mode, create, siteOptions]);

  const handleSubmit = (values, {setSubmitting, setErrors}) => {
    setSubmitting(true);
    setSubmitError(null);

    if (create) {
      const submitData = mode === 'staff'
        ? newFormModel({shape, data: {...values, role: 'staff', password: 'rhtemp23', sites: state.site.normalName}})
        : newFormModel({shape, data: {...values, password: 'rhtemp23'}});

      createUser(submitData)
        .then(() => {
          setSubmitting(false);
          onSuccess?.(getUserSuccessMessage({create, mode}));
          onClose();
        })
        .catch((e) => {
          const error = getUserSubmitError(e, {create, mode});
          setErrors(error.fieldError ? {email: error.fieldError} : {});
          setSubmitError(error);
          setSubmitting(false);
        });
    } else {
      update(user._id, newFormModel({shape, data: values}))
        .then(() => {
          setSubmitting(false);
          onSuccess?.(getUserSuccessMessage({create, mode}));
          onClose();
        })
        .catch((e) => {
          const error = getUserSubmitError(e, {create, mode});
          setErrors(error.fieldError ? {email: error.fieldError} : {});
          setSubmitError(error);
          setSubmitting(false);
        });
    }
  };

  const getTitle = () => {
    if (create) {
      return mode === 'staff' ? 'Add Staff' : 'Create User';
    }
    return mode === 'staff' ? 'Update Staff' : 'Update User';
  };

  return (
    <div>
      {shape && <Dialog
        fullWidth={true}
        maxWidth={'md'}
        open={open}
        onClose={onClose}
        aria-labelledby="max-width-dialog-title"
      >
        <Formik
          initialValues={create ?
            newFormModel({shape: shape}) :
            newFormModel({shape: shape, data: user})}
          onSubmit={handleSubmit}
          validationSchema={yup.object().shape(
            newFormModel({shape:shape, validation: true}))
          }
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              isSubmitting,
              handleChange,
              handleBlur,
              handleSubmit: formHandleSubmit,
            } = props;
            return (
              <ModalForm autoComplete="off" onSubmit={formHandleSubmit}>
                {submitError && <AlertStyled severity="error">
                  <AlertTitle>{submitError.title}</AlertTitle>
                  {submitError.messages.length === 1
                    ? submitError.messages[0]
                    : <ul>{submitError.messages.map(message => <li key={message}>{message}</li>)}</ul>}
                </AlertStyled>}
                <ModalFormHead>
                  <ModalFormH2>{getTitle()}</ModalFormH2>
                </ModalFormHead>

                <ModalFormContent>
                  <div>
                    <div>
                      {Object.values(shape).map((formConfig) => (
                        <FormElement
                          key={formConfig.id}
                          id={formConfig.id}
                          label={formConfig.label}
                          type={formConfig.type}
                          hidden={formConfig.hidden}
                          selectValues={formConfig.selectValues}
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                        />
                      ))}
                    </div>
                  </div>
                </ModalFormContent>
                <ModalFormFoot>
                  <Button
                    disabled={isSubmitting}
                    onClick={() => {
                      setSubmitError(null);
                      onClose();
                    }}
                  >Cancel
                  </Button>
                  <Button
                    style={{marginLeft: 8}}
                    disabled={isSubmitting}
                    type="submit"
                    variant="contained"
                    color="primary">{create ? <span>Add</span> : <span>Update</span>}
                  </Button>
                </ModalFormFoot>
              </ModalForm>);
          }}
        </Formik>
      </Dialog>}
    </div>
  );
}
